import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { activeChild, itemActive, linkMatches } from "../nav";
import { CountBadge, countFor, drillFor, useNavCounts } from "../hooks/useNavCounts";

const KEY = "traide-nav-more";

// Two-level sidebar with accordion behaviour: only the section you are working in stays expanded.
// Secondary features sit under a folded "More" group. Collapsed (icon-only) mode shows each section's functions in a flyout.
export function SidebarNav({ groups, collapsed, badges = {}, extra = [] }) {
  const loc = useLocation();
  const counts = useNavCounts();
  const [openHub, setOpenHub] = useState(null);
  const [more, setMore] = useState(() => localStorage.getItem(KEY) === "1");
  useEffect(() => { localStorage.setItem(KEY, more ? "1" : "0"); }, [more]);
  useEffect(() => {
    const hub = groups.flatMap((g) => g.items).find((it) => it.children && itemActive(it, loc));
    setOpenHub(hub ? hub.key : null);
    if (groups.some((g) => g.fold && g.items.some((it) => itemActive(it, loc)))) setMore(true);
  }, [loc.pathname, loc.search]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (key) => setOpenHub((o) => (o === key ? null : key));
  return (
    <>
      {groups.map((g, gi) => {
        const last = gi === groups.length - 1;
        const items = g.items.map((it) => <Item key={it.key} it={it} loc={loc} counts={counts} collapsed={collapsed} open={openHub === it.key} onToggle={() => toggle(it.key)} badge={it.badge ? badges[it.badge] : null} />);
        if (g.fold && !collapsed) {
          const active = g.items.some((it) => itemActive(it, loc));
          const badge = g.items.reduce((a, it) => a + (it.badge ? badges[it.badge] || 0 : 0), 0);
          return (
            <div key={g.group} className="pt-3" data-testid={`nav-group-${g.group.toLowerCase()}`} data-open={more ? "1" : "0"}>
              <button type="button" onClick={() => setMore((m) => !m)} className={`nav-item w-full ${active && !more ? "nav-item-active" : ""}`} aria-expanded={more} data-testid="nav-more">
                {g.icon && <g.icon size={17} strokeWidth={1.9} />}<span className="flex-1 text-left">{g.group}</span>
                {!more && badge > 0 && <span className="nav-count nav-count-warn">{badge > 99 ? "99+" : badge}</span>}
                <ChevronRight size={14} strokeWidth={2} className={`transition-transform duration-200 ${more ? "rotate-90" : ""}`} style={{ color: "var(--faint)" }} />
              </button>
              {more && <div className="mt-0.5 space-y-0.5">{items}{last && extra}</div>}
            </div>
          );
        }
        return (
          <div key={g.group} data-testid={`nav-group-${g.group.toLowerCase()}`}>
            {collapsed && gi > 0 && <div className="my-2 border-t border-line" />}
            <div className="space-y-0.5">{items}{last && extra}</div>
          </div>
        );
      })}
    </>
  );
}

function Badge({ n, collapsed }) {
  if (!n) return null;
  if (collapsed) return <span className="absolute -right-1.5 -top-1.5 h-2 w-2 rounded-full bg-amber-500" />;
  return <span className="ml-auto flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-amber-500 px-1.5 tnum text-[10px] font-bold text-zinc-950" data-testid="nav-messages-unread">{n > 99 ? "99+" : n}</span>;
}

function Item({ it, loc, counts, collapsed, open, onToggle, badge }) {
  const active = itemActive(it, loc);
  const Icon = it.icon;
  if (it.children && collapsed) return <CollapsedHub it={it} loc={loc} counts={counts} active={active} />;
  if (!it.children) {
    return (
      <NavLink to={it.to} end={it.end} className={`nav-item ${active ? "nav-item-active" : ""} ${collapsed ? "justify-center !px-0" : ""}`} data-testid={`nav-${it.key}`} title={collapsed ? it.label : undefined}>
        <span className="relative"><Icon size={17} strokeWidth={1.9} />{collapsed && <Badge n={badge} collapsed />}</span>
        {!collapsed && <span className="truncate">{it.label}</span>}
        {!collapsed && <Badge n={badge} />}
        {!collapsed && it.tag && <span className="ml-auto rounded bg-zinc-950 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-amber-400">{it.tag}</span>}
      </NavLink>
    );
  }
  const child = activeChild(it, loc);
  return (
    <div className={`nav-hub ${open ? "nav-hub-open" : ""}`} data-testid={`nav-hub-${it.key}`} data-open={open ? "1" : "0"}>
      <div className={`nav-item nav-hub-row ${active ? "nav-item-active" : ""}`}>
        <Link to={it.to} className="flex min-w-0 flex-1 items-center gap-2.5" data-testid={`nav-${it.key}`} onClick={() => { if (!open) onToggle(); }}>
          <Icon size={17} strokeWidth={1.9} /><span className="truncate">{it.label}</span>
        </Link>
        <button type="button" onClick={onToggle} className="nav-chevron" aria-label={`${open ? "Collapse" : "Expand"} ${it.label}`} aria-expanded={open} data-testid={`nav-toggle-${it.key}`}><ChevronRight size={14} strokeWidth={2} /></button>
      </div>
      <div className="nav-children" aria-hidden={!open}>
        <div className="nav-children-inner">
          {it.children.map((c) => {
            const on = child ? child.key === c.key : linkMatches(c, loc);
            return (
              <div key={c.key} className={`nav-sub-row ${on ? "nav-sub-row-active" : ""}`}>
                <Link to={c.to} className={`nav-sub ${on ? "nav-sub-active" : ""}`} aria-current={on ? "page" : undefined} data-testid={`nav-${it.key}-${c.key}`} tabIndex={open ? 0 : -1}><span className="truncate">{c.label}</span></Link>
                <CountBadge n={countFor(counts, it.key, c)} tone={c.tone} to={drillFor(it.key, c)} hint={c.hint} className="mr-1.5" testid={`nav-count-${it.key}-${c.key}`} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Icon-only hub with a flyout listing its functions (hover, focus or keyboard). Rendered through a portal so the scrolling sidebar never clips it.
function CollapsedHub({ it, loc, counts, active }) {
  const ref = useRef(null);
  const timer = useRef(null);
  const [pos, setPos] = useState(null);
  const Icon = it.icon;
  const child = activeChild(it, loc);
  const alert = it.children.reduce((a, c) => (countFor(counts, it.key, c) && c.tone ? (c.tone === "urgent" ? "urgent" : a || "warn") : a), null);
  const show = () => { clearTimeout(timer.current); const r = ref.current?.getBoundingClientRect(); if (r) setPos({ top: Math.min(r.top, window.innerHeight - 320), left: r.right + 8 }); };
  const hide = () => { clearTimeout(timer.current); timer.current = setTimeout(() => setPos(null), 140); };
  useEffect(() => () => clearTimeout(timer.current), []);
  useEffect(() => { setPos(null); }, [loc.pathname, loc.search]);
  const onKey = (e) => { if (e.key === "Escape") setPos(null); };
  return (
    <div ref={ref} className="relative" onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget) && !document.getElementById(`nav-flyout-${it.key}`)?.contains(e.relatedTarget)) hide(); }} onKeyDown={onKey} data-testid={`nav-hub-${it.key}`} data-flyout={pos ? "1" : "0"}>
      <NavLink to={it.to} className={`nav-item justify-center !px-0 ${active ? "nav-item-active" : ""}`} data-testid={`nav-${it.key}`} aria-haspopup="true" aria-expanded={!!pos} aria-label={it.label}>
        <span className="relative"><Icon size={17} strokeWidth={1.9} />{alert && <span className={`absolute -right-1.5 -top-1.5 h-2 w-2 rounded-full ${alert === "urgent" ? "bg-rose-500" : "bg-amber-500"}`} data-testid={`nav-alert-${it.key}`} />}</span>
      </NavLink>
      {pos && createPortal(
        <div id={`nav-flyout-${it.key}`} role="menu" className="nav-flyout" style={{ top: pos.top, left: pos.left }} onMouseEnter={show} onMouseLeave={hide} onKeyDown={onKey} onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget) && !ref.current?.contains(e.relatedTarget)) hide(); }} data-testid={`nav-flyout-${it.key}`}>
          <div className="nav-flyout-title"><Icon size={13} strokeWidth={2} />{it.label}</div>
          {it.children.map((c) => {
            const on = child ? child.key === c.key : linkMatches(c, loc);
            const close = () => { clearTimeout(timer.current); setPos(null); };
            return (
              <div key={c.key} className="nav-flyout-row">
                <Link to={c.to} role="menuitem" onClick={close} className={`nav-flyout-link ${on ? "nav-flyout-link-active" : ""}`} aria-current={on ? "page" : undefined} data-testid={`nav-flyout-${it.key}-${c.key}`}><span className="truncate">{c.label}</span></Link>
                <CountBadge n={countFor(counts, it.key, c)} tone={c.tone} to={drillFor(it.key, c)} hint={c.hint} onClick={close} className="mr-1" testid={`nav-flyout-count-${it.key}-${c.key}`} />
              </div>
            );
          })}
        </div>, document.body)}
    </div>
  );
}
